import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Camera, Search, Filter } from 'lucide-react';
import { useGalleryConfig } from '../context/GalleryConfigContext';
import { useGalleryTheme } from '../context/GalleryThemeContext';
import { GalleryEvent, EventFilters } from '../types/gallery';
import { galleryApiService } from '../services/galleryApiService';
import Button from '../components/common/Button';
import LoadingScreen from '../components/common/LoadingScreen';

const EventListPage: React.FC = () => {
  const { config } = useGalleryConfig();
  const { theme } = useGalleryTheme();
  const [events, setEvents] = useState<GalleryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<EventFilters>({
    sortBy: 'date',
    sortOrder: 'desc',
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadEvents = async () => {
      if (!config?.photographerId) return;

      try {
        setLoading(true);
        setError(null);

        const eventsResponse = await galleryApiService.getEvents(config.photographerId, {
          ...filters,
          search: searchQuery || undefined,
        });

        setEvents(eventsResponse.items);
      } catch (err) {
        setError('Failed to load galleries');
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [config, filters, searchQuery]);

  if (loading) {
    return <LoadingScreen message="Loading galleries..." />;
  }

  if (!config) {
    return <div>Gallery configuration not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="text-center">
        <h1
          className="text-3xl md:text-4xl font-bold mb-4"
          style={{ color: theme.colors.text.primary }}
        >
          Photo Galleries
        </h1>
        <p className="text-lg" style={{ color: theme.colors.text.secondary }}>
          Browse our collection of event photo galleries
        </p>
      </div>

      {/* Search and Filters */}
      <div
        className="flex flex-col md:flex-row items-center justify-between p-4 rounded-lg space-y-4 md:space-y-0"
        style={{ backgroundColor: theme.colors.surface }}
      >
        {/* Search */}
        <div className="relative w-full md:w-auto">
          <Search
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2"
            style={{ color: theme.colors.text.muted }}
          />
          <input
            type="text"
            placeholder="Search galleries..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 focus:outline-none focus:ring-2"
            style={{
              borderColor: theme.colors.border,
            }}
          />
        </div>

        {/* Sort Options */}
        <div className="flex items-center space-x-2">
          <Filter size={16} style={{ color: theme.colors.text.muted }} />
          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={e => {
              const [sortBy, sortOrder] = e.target.value.split('-') as [any, any];
              setFilters(prev => ({ ...prev, sortBy, sortOrder }));
            }}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
            style={{
              borderColor: theme.colors.border,
            }}
          >
            <option value="eventDate-desc">Newest First</option>
            <option value="eventDate-asc">Oldest First</option>
            <option value="title-asc">Title A-Z</option>
            <option value="title-desc">Title Z-A</option>
            <option value="created-desc">Recently Added</option>
          </select>
        </div>
      </div>

      {/* Events Grid */}
      {events.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map(event => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Camera size={64} className="mx-auto mb-4" style={{ color: theme.colors.text.muted }} />
          <h3 className="text-xl font-semibold mb-2" style={{ color: theme.colors.text.primary }}>
            No galleries found
          </h3>
          <p style={{ color: theme.colors.text.secondary }}>
            {searchQuery
              ? 'Try adjusting your search terms'
              : 'Check back later for new photo galleries'}
          </p>
        </div>
      )}

      {error && (
        <div
          className="text-center py-8 px-4 rounded-lg"
          style={{
            backgroundColor: theme.colors.error + '10',
            color: theme.colors.error,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

// Event Card Component
interface EventCardProps {
  event: GalleryEvent;
}

const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const { theme } = useGalleryTheme();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Link to={`/events/${event.id}`} className="group block">
      <div
        className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform group-hover:scale-105"
        style={{ backgroundColor: theme.colors.surface }}
      >
        {/* Cover Image */}
        <div className="aspect-video bg-gray-200 overflow-hidden">
          {event.coverImage ? (
            <img
              src={event.coverImage}
              alt={event.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: theme.colors.background }}
            >
              <Camera size={48} style={{ color: theme.colors.text.muted }} />
            </div>
          )}
        </div>

        {/* Event Info */}
        <div className="p-6">
          <h3
            className="text-xl font-bold mb-2 group-hover:text-opacity-80 transition-colors"
            style={{ color: theme.colors.text.primary }}
          >
            {event.title}
          </h3>

          {event.description && (
            <p className="text-sm mb-4 line-clamp-2" style={{ color: theme.colors.text.secondary }}>
              {event.description}
            </p>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2" style={{ color: theme.colors.text.muted }}>
              <Calendar size={16} />
              <span>{formatDate(event.eventDate)}</span>
            </div>

            {event.location && (
              <div
                className="flex items-center space-x-2"
                style={{ color: theme.colors.text.muted }}
              >
                <MapPin size={16} />
                <span>{event.location}</span>
              </div>
            )}

            <div className="flex items-center space-x-2" style={{ color: theme.colors.text.muted }}>
              <Camera size={16} />
              <span>{event.totalPhotos} photos</span>
            </div>
          </div>

          {/* View Gallery Button */}
          <div className="mt-4">
            <Button variant="primary" size="sm" fullWidth className="group-hover:bg-opacity-90">
              View Gallery
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EventListPage;
